/**
 * Main TypeScript entry point for issify.com
 */

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  initEmailLinks();
  initDynamicYear();
  initScrollAnimations();
});

/**
 * Email obfuscation to prevent scraping
 * Builds email addresses from data attributes at runtime
 * Provides both click-to-email and copy-to-clipboard functionality
 */
function initEmailLinks(): void {
  const emailElements = document.querySelectorAll('.email[data-user][data-domain]');

  emailElements.forEach((element) => {
    const user = element.getAttribute('data-user');
    const domain = element.getAttribute('data-domain');

    if (user && domain) {
      const email = `${user}@${domain}`;

      // Create wrapper for email + copy button
      const wrapper = document.createElement('span');
      wrapper.className = 'email-wrapper';

      // Create clickable link
      const link = document.createElement('a');
      link.href = `mailto:${email}`;
      link.textContent = email;
      link.className = 'email-link';
      link.title = 'Click to open in email client';

      // Create copy button with icon
      const copyBtn = document.createElement('button');
      copyBtn.className = 'copy-btn';
      copyBtn.innerHTML = '📋';
      copyBtn.title = 'Copy email to clipboard';
      copyBtn.type = 'button';
      copyBtn.setAttribute('aria-label', 'Copy email address');

      copyBtn.addEventListener('click', (e) => {
        e.preventDefault();
        copyToClipboard(email, copyBtn);
      });

      // Also keep right-click for power users
      link.addEventListener('contextmenu', (e) => {
        e.preventDefault();
        copyToClipboard(email, copyBtn);
      });

      wrapper.appendChild(link);
      wrapper.appendChild(copyBtn);

      // Replace the placeholder with the wrapper
      element.replaceWith(wrapper);
    }
  });
}

/**
 * Copy text to clipboard with user feedback
 */
function copyToClipboard(text: string, button?: HTMLElement): void {
  navigator.clipboard
    .writeText(text)
    .then(() => {
      // Change button temporarily if provided
      if (button) {
        const originalContent = button.innerHTML;
        button.innerHTML = '✓';
        button.style.background = '#10b981';

        setTimeout(() => {
          button.innerHTML = originalContent;
          button.style.background = '';
        }, 1500);
      }

      // Show temporary feedback
      const feedback = document.createElement('div');
      feedback.textContent = '✓ Email copied to clipboard';
      feedback.style.cssText = `
        position: fixed;
        bottom: 20px;
        right: 20px;
        background: #10b981;
        color: white;
        padding: 12px 20px;
        border-radius: 4px;
        font-size: 14px;
        box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
        z-index: 1000;
        animation: slideIn 0.3s ease-out;
      `;

      document.body.appendChild(feedback);

      // Remove after 2 seconds
      setTimeout(() => {
        feedback.style.animation = 'slideOut 0.3s ease-out';
        setTimeout(() => feedback.remove(), 300);
      }, 2000);
    })
    .catch((err) => {
      console.error('Failed to copy email:', err);
    });
}

/**
 * Set current year dynamically in copyright
 */
function initDynamicYear(): void {
  const yearElement = document.getElementById('year');
  if (yearElement) {
    yearElement.textContent = new Date().getFullYear().toString();
  }
}

/**
 * Animate elements on scroll using Intersection Observer
 * SEO-friendly: all content is in HTML, animations are visual only
 */
function initScrollAnimations(): void {
  const observerOptions = {
    threshold: 0.1,
    rootMargin: '0px 0px -50px 0px',
  };

  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        entry.target.classList.add('visible');
        observer.unobserve(entry.target); // Only animate once
      }
    });
  }, observerOptions);

  // Observe all quote cards
  const quoteCards = document.querySelectorAll('.quote-card');
  quoteCards.forEach((card) => observer.observe(card));
}
